import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["columnInputPlaceholder", "header", "items", "multiSelection", "parentId", "path", "setMultiSelection", "setPath"];
//
// Copyright IBM Corp. 2022
//
// This source code is licensed under the Apache-2.0 license found in the
// LICENSE file in the root directory of this source tree.
//
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Search, Tag, OverflowMenu, Checkbox } from 'carbon-components-react';
import { Filter32 } from '@carbon/icons-react';
import { pkg, carbon } from '../../settings';
import { AddSelectList } from './AddSelectList';
import { AddSelectSort } from './AddSelectSort';
import { sortItems } from './add-select-utils';
import { useItemSort } from './hooks/useItemSort';
import uuidv4 from '../../global/js/utils/uuidv4';
import useParentSelect from './hooks/useParentSelect';
var blockClass = "".concat(pkg.prefix, "--add-select");
var colClass = "".concat(blockClass, "__column");
var componentName = 'AddSelectColumn';
export var AddSelectColumn = function AddSelectColumn(_ref) {
  var columnInputPlaceholder = _ref.columnInputPlaceholder,
      header = _ref.header,
      items = _ref.items,
      multiSelection = _ref.multiSelection,
      parentId = _ref.parentId,
      path = _ref.path,
      setMultiSelection = _ref.setMultiSelection,
      setPath = _ref.setPath,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useParentSelect = useParentSelect(),
      parentSelected = _useParentSelect.parentSelected,
      setParentSelected = _useParentSelect.setParentSelected;

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      searchTerm = _useState2[0],
      setSearchTerm = _useState2[1];

  var _useItemSort = useItemSort(),
      sortDirection = _useItemSort.sortDirection,
      setSortDirection = _useItemSort.setSortDirection,
      sortAttribute = _useItemSort.sortAttribute,
      setSortAttribute = _useItemSort.setSortAttribute;

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      filters = _useState4[0],
      setFilters = _useState4[1];

  var entries = items.entries,
      filterBy = items.filterBy,
      sortBy = items.sortBy;

  var getSelectedItem = function getSelectedItem() {
    var _entries$find;

    var parentInPath = path.find(function (entry) {
      return entry.parentId === parentId;
    });
    /**
     * this check helps ensure that when the state of the open columns is cleared by global search
     * that the columns are rebuilt from the data in the path array
     */

    if (parentInPath && !parentSelected) {
      return entries.find(function (item) {
        return item.id === parentInPath.id;
      });
    }

    return (_entries$find = entries.find(function (item) {
      return item.id === parentSelected;
    })) !== null && _entries$find !== void 0 ? _entries$find : null;
  };

  var selectedItem = getSelectedItem();
  /**
   * this check helps prevent children from already being open when you switch from parents
   * on the same level. for example- using the storybook example, if you click on folder 1,
   * file 1, folder 2, and then folder 1 again file 1 children shouldn't be expanded. this
   * check ensures that when a user navigates to parents on the same level that the open state
   * of their children is cleared by referencing the path array.
   */

  var itemInPath = selectedItem && path.find(function (entry) {
    return entry.id === selectedItem.id;
  });
  var allSelected = entries.every(function (item) {
    return multiSelection.includes(item.id);
  }); // filtering

  var filterByOpts = filterBy ? entries.map(function (item) {
    return item[filterBy];
  }) : [];

  var selectAllHandler = function selectAllHandler(checked) {
    var itemIds = entries.map(function (item) {
      return item.id;
    });

    if (checked) {
      var newSelections = _toConsumableArray(new Set([].concat(_toConsumableArray(multiSelection), _toConsumableArray(itemIds))));

      setMultiSelection(newSelections);
    } else {
      var newItems = multiSelection.filter(function (i) {
        return !itemIds.includes(i);
      });
      setMultiSelection(newItems);
    }
  };

  var filterHandler = function filterHandler(checked, opt) {
    if (checked) {
      var newFilters = [].concat(_toConsumableArray(filters), [opt]);
      setFilters(newFilters);
    } else {
      var _newFilters = filters.filter(function (o) {
        return o !== opt;
      });

      setFilters(_newFilters);
    }
  }; // filter and sort array functions


  var filterBySearch = function filterBySearch(item) {
    return item.title.toLowerCase().includes(searchTerm);
  };

  var filterByAttribute = function filterByAttribute(item) {
    if (filters.length === 0) {
      return true;
    }

    var filterBy = item.filterBy;
    var filterByValue = item[filterBy];
    return filters.some(function (filter) {
      return filter === filterByValue;
    });
  };

  var sortFn = sortItems(sortAttribute, sortDirection);
  var colItems = entries.filter(filterBySearch) // first check if the item meets the search
  .filter(filterByAttribute) // then check if the item is included in the filter
  .sort(sortFn); // then sort the items by whatever criteria

  var parentSelectionHandler = function parentSelectionHandler(id, title) {
    setParentSelected(id);
    setPath(id, title, parentId);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: colClass
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(colClass, "-search-bar")
  }, /*#__PURE__*/React.createElement(Search, {
    value: searchTerm,
    onChange: function onChange(e) {
      return setSearchTerm(e.target.value);
    },
    light: true,
    placeholder: columnInputPlaceholder,
    className: "".concat(colClass, "-input"),
    id: uuidv4(),
    labelText: columnInputPlaceholder,
    size: "lg"
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(colClass, "-sort-filter")
  }, /*#__PURE__*/React.createElement(AddSelectSort, {
    items: entries,
    setSortAttribute: setSortAttribute,
    setSortDirection: setSortDirection,
    sortAttribute: sortAttribute,
    sortDirection: sortDirection,
    sortBy: sortBy
  }), filterByOpts.length > 0 && /*#__PURE__*/React.createElement(OverflowMenu, {
    renderIcon: Filter32,
    className: "".concat(colClass, "-overflow"),
    flipped: true
  }, filterByOpts.map(function (opt) {
    return /*#__PURE__*/React.createElement("div", {
      key: opt,
      className: "".concat(carbon.prefix, "--overflow-menu-options__option")
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(carbon.prefix, "--overflow-menu-options__btn")
    }, /*#__PURE__*/React.createElement(Checkbox, {
      id: opt,
      labelText: opt,
      onChange: function onChange(checked) {
        return filterHandler(checked, opt);
      },
      checked: filters.find(function (o) {
        return o === opt;
      }) ? true : false
    })));
  })))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(blockClass, "__tag-container")
  }, /*#__PURE__*/React.createElement(Checkbox, {
    id: "".concat(uuidv4(), "-select-all"),
    className: "".concat(colClass, "__select-all"),
    checked: allSelected,
    onChange: selectAllHandler,
    labelText: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "".concat(blockClass, "__tag-container-label")
    }, header), /*#__PURE__*/React.createElement(Tag, {
      type: "gray",
      size: "sm"
    }, colItems.length))
  })), /*#__PURE__*/React.createElement(AddSelectList, _extends({}, props, {
    filteredItems: colItems,
    setMultiSelection: setMultiSelection,
    multiSelection: multiSelection,
    setParentSelected: parentSelectionHandler
  }))), selectedItem && itemInPath && /*#__PURE__*/React.createElement(AddSelectColumn, _extends({
    columnInputPlaceholder: columnInputPlaceholder,
    header: selectedItem.title,
    items: selectedItem.children,
    multiSelection: multiSelection,
    setMultiSelection: setMultiSelection,
    parentId: selectedItem.id,
    setPath: setPath,
    path: path
  }, props)));
};
AddSelectColumn.propTypes = {
  columnInputPlaceholder: PropTypes.string,
  header: PropTypes.string,
  items: PropTypes.object,
  multiSelection: PropTypes.array,
  parentId: PropTypes.string,
  path: PropTypes.array,
  setMultiSelection: PropTypes.func,
  setPath: PropTypes.func
};
AddSelectColumn.displayName = componentName;